import React, { useEffect, useState } from 'react';
import api from '../api';

/**
 * The one way to delete something in Agromate (no browser confirm). It always shows what uses the row
 * (from `usageUrl`, e.g. "Used by 312 trials and 4 projects."). A row in use cannot be deleted - only
 * "Archive instead" is offered (when `onArchive` is given). Otherwise Delete is enabled once the name
 * (or number) has been typed. Deletes are soft.
 *
 * Props:
 * - open, onClose
 * - title: e.g. "Delete crop" (also the Delete button's text); heading "<title> <name>?" - or, for a
 *   row in use, "<subject> <name> is in use" (it is not going to be deleted)
 * - subject: what the row is, capitalised (e.g. "CO"), for that heading
 * - name: what must be typed; nameLabel: what it is called ("name", "trial number" ...)
 * - usageUrl: GET it for { text, in_use, can_delete } (master data) or { text } (a note, e.g. a diary
 *   entry's "This also unticks Harvest on the trial.")
 * - description: a sentence on what deleting does
 * - onDelete(): performs the delete (async); a 409 from it shows its message and offers archiving
 * - onArchive(): archives instead (async), optional
 * - archiveOnly: no delete at all (e.g. customers) - shows the usage and "Archive"
 * - onDone(action): after 'deleted' or 'archived'
 */
export default function ConfirmDeleteDialog({
  open, onClose, title, subject = null, name, nameLabel = 'name', usageUrl = null, description = null,
  onDelete, onArchive = null, archiveOnly = false, onDone,
}) {
  const [typed, setTyped] = useState('');
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTyped('');
    setError(null);
    setUsage(null);
    if (!usageUrl) return;
    let cancelled = false;
    setLoadingUsage(true);
    api.get(usageUrl)
      .then((res) => { if (!cancelled) setUsage(res.data); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || 'Could not tell what uses this.'); })
      .finally(() => { if (!cancelled) setLoadingUsage(false); });
    return () => { cancelled = true; };
  }, [open, usageUrl]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape' && !busy) onClose?.();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, busy, onClose]);

  const expected = String(name ?? '');
  const inUse = Boolean(usage?.in_use);
  const canDelete = !archiveOnly && (usage?.can_delete ?? true) && !inUse;
  const confirmed = expected !== '' && typed.trim() === expected;

  async function run(action, fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone?.(action);
    } catch (err) {
      const data = err?.response?.data;
      // In use after all (409): show what uses it, offer archiving
      if (err?.response?.status === 409) setUsage({ ...data, in_use: true, can_delete: false });
      setError(data?.message || `Failed to ${action === 'archived' ? 'archive' : 'delete'}`);
      setBusy(false);
    }
  }

  function handleDelete(e) {
    e.preventDefault();
    if (!canDelete || !confirmed) return;
    run('deleted', onDelete);
  }

  // A row in use is not going to be deleted: say so rather than ask "Delete ...?"
  const inUseHeading = inUse && !archiveOnly && subject;
  const heading = inUseHeading ? `${subject} ${expected} is in use` : `${title} ${expected}?`;

  if (!open) return null;
  return (
    // whitespace-normal/text-left: the dialog is often rendered inside a nowrap or centred table cell
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 whitespace-normal text-left"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <div role="dialog" aria-label={heading} className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 w-full max-w-md break-words">
        <h2 className={`text-xl font-bold mb-2 ${inUseHeading || archiveOnly ? 'text-amber-700 dark:text-amber-400' : 'text-red-700 dark:text-red-400'}`}>{heading}</h2>
        {description && <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{description}</p>}
        {usageUrl && (
          <p className="text-sm font-medium mb-4" data-testid="delete-usage">
            {loadingUsage ? 'Checking what uses it…' : usage?.text}
          </p>
        )}
        {!canDelete && !loadingUsage && usage && !archiveOnly && (
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
            It cannot be deleted while it is in use.{onArchive ? ' Archive it instead: it is then hidden for new trials but kept on the existing ones.' : ''}
          </p>
        )}
        {archiveOnly && (
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
            Archived, it is hidden for new trials but kept on the existing ones. It can be reactivated.
          </p>
        )}
        <form onSubmit={handleDelete} className="space-y-4">
          {canDelete && (
            <div>
              <label htmlFor="confirm-delete-name" className="block text-sm font-medium mb-1">
                Type the {nameLabel} <span className="font-mono">{expected}</span> to confirm
              </label>
              <input
                id="confirm-delete-name"
                type="text"
                autoComplete="off"
                autoFocus
                className="w-full border rounded px-2 py-1 dark:bg-gray-800 dark:border-gray-700"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
            </div>
          )}
          {error && <div role="alert" className="text-red-500 text-sm">{error}</div>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              onClick={onClose}
              disabled={busy}
            >Cancel</button>
            {onArchive && (!canDelete || archiveOnly) && !loadingUsage && (
              <button
                type="button"
                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50"
                disabled={busy}
                onClick={() => run('archived', onArchive)}
              >{archiveOnly ? 'Archive' : 'Archive instead'}</button>
            )}
            {canDelete && (
              <button
                type="submit"
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!confirmed || busy || loadingUsage}
              >{busy ? 'Deleting...' : title}</button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
